import React, {
	Dispatch,
	memo,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "../../../../services";
import { useTypedSelector } from "../../../../redux/store";
import { useTaxiServiceFilterAccessControl } from "../../../../hooks";
import MultiSelectWithModalBase, {
	MultiSelectWithModal,
} from "../MultiSelectWithModal";

const MultiSelectSourceCallSingWithModalBase: React.FC<
	MultiSelectSourceCallSingWithModalBase.Props
> = ({
	value,
	onChange,
	disabled,
	titleText,
	required,
	modalSetting,
	error = "",
	sort,
	taxiServiceIds,
	isOnCheckBox = true,
	...props
}) => {
	const { t } = useTranslation();
	const { accessTaxiServicesIds } = useTaxiServiceFilterAccessControl<any>();

	const lang = useTypedSelector((state) => state.session.language);

	const [errorInternal, setErrorInternal] = useState<string | boolean>("");
	const [items, setItems] = useState<
		Modal.ModelSimpleForMultySelectModalDispatcher[]
	>([]);
	const [loading, setLoading] = useState(false);

	const optionsService = useMemo<
		Omit<Modal.SubscribeOptions, "typedb">
	>(() => {
		const result: Omit<Modal.SubscribeOptions, "typedb"> = {
			lang,
			taxiServiceIds: taxiServiceIds || accessTaxiServicesIds,
		};

		return result;
	}, [accessTaxiServicesIds, lang, taxiServiceIds]);

	useEffect(() => {
		if (isOnCheckBox === false) return () => {};

		let mounted = true;

		const loadData = async () => {
			setLoading(true);
			try {
				const data = await Modal.getAllDispatcher(optionsService);

				if (mounted) {
					setItems(data?.items ?? []);
				}
			} catch (err) {
				console.error("Failed to fetch Modal.getAll:", err);
				if (mounted) {
					setItems([]);
				}
			} finally {
				if (mounted) setLoading(false);
			}
		};

		loadData();

		return () => {
			mounted = false;
		};
	}, [isOnCheckBox, optionsService]);

	useEffect(() => {
		setErrorInternal(error);
	}, [error]);

	const formatAliasAndName = (
		item: Modal.ModelSimpleForMultySelectModalDispatcher,
	): string => {
		if (!item || typeof item !== "object") return "";

		const alias = item.alias?.trim();

		const fullName = [item?.lastName, item?.firstName, item?.fatherName]
			.filter((v) => !!v?.trim())
			.join(" ");

		if (alias && fullName) return `${alias} – ${fullName}`;
		return alias || fullName || "";
	};

	const selectOptions = useMemo<MultiSelectWithModalBase.Value>(
		() =>
			[...items]
				.sort((a, b) => {
					const aNum = Number(a.alias) || Infinity;
					const bNum = Number(b.alias) || Infinity;
					return aNum - bNum;
				})
				.map((item) => {
					const label = formatAliasAndName(item);

					return {
						key: item.id,
						label,
						value: Number(item.alias || -1) || -1,
						name: label,
						data: item,
					};
				}),
		[items],
	);

	const selected = useMemo<MultiSelectWithModalBase.Value>(
		() =>
			selectOptions.filter((item) =>
				value.find((data) => data === item.value),
			),

		[selectOptions, value],
	);

	const selectOnChange = useCallback(
		(newValue) => {
			setErrorInternal("");

			const ids: number[] = newValue.map((item) => item.value);

			onChange(ids);
		},
		[onChange],
	);

	const texts = useMemo(
		() => ({
			title: t(titleText),
			allText: t("all_customerCallSing"),
			allActiveText: t("all_active_customerCallSing"),
		}),
		[t, titleText],
	);

	const allText = useMemo(() => texts.allText, [texts.allText]);

	const allActiveText = useMemo(
		() => texts.allActiveText,
		[texts.allActiveText],
	);

	return (
		<MultiSelectWithModal
			value={selected}
			onChange={selectOnChange}
			options={selectOptions}
			error={errorInternal}
			modalSetting={modalSetting}
			disabled={disabled}
			title={texts.title}
			allText={allText}
			allActiveText={allActiveText}
			required={required}
			pullUpItemInsideArray
			hiddenButton={false}
			showSelectAll
			sort={{
				show: true,
				active: true,
				...(sort || {}),
			}}
			loadingList={loading}
			{...props}
		/>
	);
};

export const MultiSelectSourceCallSingWithModal = memo(
	MultiSelectSourceCallSingWithModalBase,
);

declare namespace MultiSelectSourceCallSingWithModalBase {
	interface Props extends MultiSelectWithModalBase.Setting {
		value: Value;
		onChange: Dispatch<Value>;
		titleText: string | string[];
		modalSetting?: MultiSelectWithModalBase.ListSelectProps["modalSetting"];
		taxiServiceIds?: number[];
		isOnCheckBox?: boolean;
	}

	type Value = number[];
}

export default MultiSelectSourceCallSingWithModalBase;
